import * as ToolbarPrimitive from '@radix-ui/react-toolbar'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../utils'
import {
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuSeparator,
} from './dropdown-menu'
import { Icon } from './icon'
import { Separator } from './separator'
import { Tooltip, TooltipTrigger } from './tooltip'

export const Toolbar = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof ToolbarPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <ToolbarPrimitive.Root
      className={cn('relative flex select-none items-center', className)}
      ref={ref}
      {...props}
    />
  )
})

export const ToolbarToggleGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof ToolbarPrimitive.ToolbarToggleGroup>
>(({ className, ...props }, ref) => {
  return (
    <ToolbarPrimitive.ToolbarToggleGroup
      className={cn('flex items-center', className)}
      {...props}
      ref={ref}
    />
  )
})

export const ToolbarLink = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<typeof ToolbarPrimitive.Link>
>(({ className, ...props }, ref) => {
  return (
    <ToolbarPrimitive.Link
      className={cn('font-medium underline underline-offset-4', className)}
      {...props}
      ref={ref}
    />
  )
})

export const ToolbarSeparator = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof ToolbarPrimitive.Separator>
>(({ className, ...props }, ref) => {
  return (
    <ToolbarPrimitive.Separator
      className={cn('bg-border mx-2 my-1 w-px shrink-0', className)}
      {...props}
      ref={ref}
    />
  )
})

// From toggleVariants
const toolbarButtonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-[color,box-shadow] outline-none hover:bg-muted hover:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-checked:bg-accent aria-checked:text-accent-foreground aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        default: 'h-9 min-w-9 px-2',
        lg: 'h-10 min-w-10 px-2.5',
        sm: 'h-8 min-w-8 px-1.5',
      },
      variant: {
        default: 'bg-transparent',
        outline:
          'border border-input bg-transparent shadow-xs hover:bg-accent hover:text-accent-foreground',
      },
    },
  },
)

const dropdownArrowVariants = cva(
  cn(
    'inline-flex items-center justify-center rounded-r-md text-sm font-medium text-foreground transition-colors disabled:pointer-events-none disabled:opacity-50',
  ),
  {
    defaultVariants: {
      size: 'sm',
      variant: 'default',
    },
    variants: {
      size: {
        default: 'h-9 w-6',
        lg: 'h-10 w-8',
        sm: 'h-8 w-4',
      },
      variant: {
        default:
          'bg-transparent hover:bg-muted hover:text-muted-foreground aria-checked:bg-accent aria-checked:text-accent-foreground',
        outline:
          'border border-l-0 border-input bg-transparent hover:bg-accent hover:text-accent-foreground',
      },
    },
  },
)

type ToolbarButtonProps = {
  isDropdown?: boolean
  pressed?: boolean
} & Omit<React.ComponentPropsWithoutRef<typeof ToolbarToggleItem>, 'asChild' | 'value'> &
  VariantProps<typeof toolbarButtonVariants>

export const ToolbarButton = withTooltip(
  React.forwardRef<HTMLButtonElement, ToolbarButtonProps>(
    (
      { children, className, isDropdown, pressed, size = 'sm', variant, ...props },
      ref,
    ) => {
      return typeof pressed === 'boolean' ? (
        <ToolbarToggleGroup disabled={props.disabled} value="single" type="single">
          <ToolbarToggleItem
            className={cn(
              toolbarButtonVariants({
                size,
                variant,
              }),
              isDropdown && 'justify-between gap-1 pr-1',
              className,
            )}
            value={pressed ? 'single' : ''}
            {...props}
            ref={ref}
          >
            {isDropdown ? (
              <>
                <div className="flex flex-1 items-center gap-2 whitespace-nowrap">
                  {children}
                </div>
                <div>
                  <Icon
                    name="caret-down"
                    className="text-muted-foreground size-3.5"
                    data-icon
                  />
                </div>
              </>
            ) : (
              children
            )}
          </ToolbarToggleItem>
        </ToolbarToggleGroup>
      ) : (
        <ToolbarPrimitive.Button
          className={cn(
            toolbarButtonVariants({
              size,
              variant,
            }),
            isDropdown && 'pr-1',
            className,
          )}
          {...props}
        >
          {children}
        </ToolbarPrimitive.Button>
      )
    },
  ),
)

export function ToolbarSplitButton({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof ToolbarButton>) {
  return (
    <ToolbarButton
      className={cn('group flex gap-0 px-0 hover:bg-transparent', className)}
      {...props}
    />
  )
}

type ToolbarSplitButtonPrimaryProps = Omit<
  React.ComponentPropsWithoutRef<typeof ToolbarToggleItem>,
  'value'
> &
  VariantProps<typeof toolbarButtonVariants>

export const ToolbarSplitButtonPrimary = React.forwardRef<
  HTMLSpanElement,
  ToolbarSplitButtonPrimaryProps
>(({ children, className, size = 'sm', variant, ...props }, ref) => {
  return (
    <span
      className={cn(
        toolbarButtonVariants({
          size,
          variant,
        }),
        'rounded-r-none',
        'group-data-[pressed=true]:bg-accent group-data-[pressed=true]:text-accent-foreground',
        className,
      )}
      {...props}
      ref={ref}
    >
      {children}
    </span>
  )
})

export function ToolbarSplitButtonSecondary({
  className,
  size,
  variant,
  ...props
}: React.ComponentPropsWithoutRef<'span'> & VariantProps<typeof dropdownArrowVariants>) {
  return (
    <span
      className={cn(
        dropdownArrowVariants({
          size,
          variant,
        }),
        'group-data-[pressed=true]:bg-accent group-data-[pressed=true]:text-accent-foreground',
        className,
      )}
      onClick={(e) => e.stopPropagation()}
      role="button"
      {...props}
    >
      <Icon name="caret-down" className="text-muted-foreground size-3.5" data-icon />
    </span>
  )
}

export const ToolbarToggleItem = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof ToolbarPrimitive.ToggleItem> &
    VariantProps<typeof toolbarButtonVariants>
>(({ className, size = 'sm', variant, ...props }, ref) => {
  return (
    <ToolbarPrimitive.ToggleItem
      className={cn(toolbarButtonVariants({ size, variant }), className)}
      {...props}
      ref={ref}
    />
  )
})

export function ToolbarGroup({ children, className }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'group/toolbar-group',
        'relative hidden has-[button]:flex',
        className,
      )}
    >
      <div className="flex items-center">{children}</div>

      <div className="group-last/toolbar-group:hidden! mx-1.5 py-0.5">
        <Separator orientation="vertical" />
      </div>
    </div>
  )
}

type TooltipProps<T extends React.ElementType> = {
  tooltip?: React.ReactNode
  tooltipContentProps?: Omit<
    React.ComponentPropsWithoutRef<typeof TooltipContent>,
    'children'
  >
  tooltipProps?: Omit<React.ComponentPropsWithoutRef<typeof Tooltip>, 'children'>
  tooltipTriggerProps?: React.ComponentPropsWithoutRef<typeof TooltipTrigger>
} & React.ComponentProps<T>

function withTooltip<T extends React.ElementType>(Component: T) {
  return function ExtendComponent({
    tooltip,
    tooltipContentProps,
    tooltipProps,
    tooltipTriggerProps,
    ...props
  }: TooltipProps<T>) {
    const [mounted, setMounted] = React.useState(false)

    React.useEffect(() => {
      setMounted(true)
    }, [])

    const component = <Component {...(props as React.ComponentProps<T>)} />

    if (tooltip && mounted) {
      return (
        <Tooltip {...tooltipProps}>
          <TooltipTrigger asChild {...tooltipTriggerProps}>
            {component}
          </TooltipTrigger>

          <TooltipContent {...tooltipContentProps}>{tooltip}</TooltipContent>
        </Tooltip>
      )
    }

    return component
  }
}

const TooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof TooltipPrimitive.Content>
>(
  (
    {
      children,
      className,
      // CHANGE
      sideOffset = 4,
      ...props
    },
    ref,
  ) => {
    return (
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          className={cn(
            'origin-(--radix-tooltip-content-transform-origin) bg-primary text-primary-foreground z-50 w-fit text-balance rounded-md px-3 py-1.5 text-xs',
            className,
          )}
          data-slot="tooltip-content"
          sideOffset={sideOffset}
          {...props}
          ref={ref}
        >
          {children}
          {/* CHANGE */}
          {/* <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-primary fill-primary" /> */}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    )
  },
)

export const ToolbarMenuGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof DropdownMenuRadioGroup> & { label?: string }
>(({ children, className, label, ...props }, ref) => {
  return (
    <>
      <DropdownMenuSeparator
        className={cn(
          'hidden',
          'mb-0 shrink-0 peer-has-[[role=menuitem]]/menu-group:block peer-has-[[role=menuitemradio]]/menu-group:block peer-has-[[role=option]]/menu-group:block',
        )}
      />

      <DropdownMenuRadioGroup
        {...props}
        ref={ref}
        className={cn(
          'hidden',
          'peer/menu-group group/menu-group my-1.5 has-[[role=menuitem]]:block has-[[role=menuitemradio]]:block has-[[role=option]]:block',
          className,
        )}
      >
        {label && (
          <DropdownMenuLabel className="text-muted-foreground select-none text-xs font-semibold">
            {label}
          </DropdownMenuLabel>
        )}
        {children}
      </DropdownMenuRadioGroup>
    </>
  )
})
